// Item-form validation. Pure and import-free so the node harness (validate.test.mjs) can compile
// and exercise this exact file; the editor renders each issue under its own field.
//
// Messages are English keys — the UI passes them through i18n's t() (see i18n/cs.ts).

export interface ValidatedField {
  key: string;
  label: string;
  type: string;
  value: string;
}

export interface ValidationIssue {
  /** Field key the issue belongs to, or "name" for the item name. */
  key: string;
  label: string;
  message: string;
  /** Errors block saving; warnings are advisory and never block. */
  severity: "error" | "warning";
}

/** Item types where a missing address is worth pointing out (they are connection targets). */
const NEEDS_ADDRESS: Record<string, string[]> = {
  ssh: ["host"],
  rdp: ["host"],
  vpn: ["server_address"],
  database: ["host"],
  network_device: ["mgmt_address", "mgmt_url"],
};

function portIssue(value: string): string | null {
  if (!/^\d+$/.test(value)) return "Port must be a number";
  const n = Number(value);
  return n >= 1 && n <= 65535 ? null : "Port must be between 1 and 65535";
}

function urlIssue(value: string): string | null {
  // Accept a scheme-less host ("vault.example.com/path") by testing a prefixed copy — the stored
  // value is never rewritten, the user's text is theirs.
  const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(value) ? value : `https://${value}`;
  try {
    const u = new URL(candidate);
    return u.hostname ? null : "This does not look like a valid URL";
  } catch {
    return "This does not look like a valid URL";
  }
}

/**
 * Validate one item. Returns issues in form order — name first, then fields — so the editor can
 * focus the first one. Empty optional fields never produce an issue.
 */
export function validateItem(item: { type: string; name: string; fields: ValidatedField[] }): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!item.name.trim()) {
    issues.push({ key: "name", label: "Name", message: "Name is required", severity: "error" });
  }
  for (const f of item.fields) {
    const value = f.value.trim();
    if (!value) continue;
    let message: string | null = null;
    if (f.type === "port") message = portIssue(value);
    else if (f.type === "url") message = urlIssue(value);
    if (message) issues.push({ key: f.key, label: f.label, message, severity: "error" });
  }
  const wanted = NEEDS_ADDRESS[item.type];
  if (wanted) {
    const has = wanted.some((k) => (item.fields.find((f) => f.key === k)?.value ?? "").trim());
    if (!has) {
      const first = item.fields.find((f) => f.key === wanted[0]);
      if (first) {
        issues.push({
          key: first.key,
          label: first.label,
          message: "Without an address this item cannot be launched",
          severity: "warning",
        });
      }
    }
  }
  return issues;
}

/** Only errors block saving. */
export function blocking(issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => i.severity === "error");
}
